/**
 * 自前の最小 QR コードエンコーダ (B5、Phase 6)。
 *
 * 2FA セットアップで `otpauth://` URI を QR 表示するために使う。otpauth URI は
 * TOTP シークレットを含む機微情報のため、**外部 QR API には絶対に渡さず**
 * クライアント内で完結させる。外部 npm 依存も持たない (Docker named-volume の
 * 同期問題を避ける方針 — `fflate` / `@aws-sdk` 撤去と同じ)。
 *
 * 仕様: ISO/IEC 18004。バイトモード固定、誤り訂正レベル M、バージョン 1-10
 * (otpauth URI の長さ ~100-200 byte を十分カバー)。アルゴリズムは Nayuki の
 * QR-Code-generator (MIT, public reference) を TypeScript へ移植・縮小したもの。
 */

/** 誤り訂正レベル M の 1 ブロックあたり EC コードワード数 (バージョン 1-10)。 */
const ECC_CODEWORDS_PER_BLOCK = [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26]

/** 誤り訂正レベル M のブロック数 (バージョン 1-10)。 */
const NUM_ERROR_CORRECTION_BLOCKS = [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5]

const MIN_VERSION = 1
const MAX_VERSION = 10

/** バージョンごとの「データ + EC」モジュール総数 (bit)。 */
const getNumRawDataModules = (ver: number): number => {
  let result = (16 * ver + 128) * ver + 64
  if (ver >= 2) {
    const numAlign = Math.floor(ver / 7) + 2

    result -= (25 * numAlign - 10) * numAlign - 55
    if (ver >= 7) result -= 36
  }

  return result
}

/** バージョンごとのデータコードワード数 (EC レベル M)。 */
const getNumDataCodewords = (ver: number): number =>
  Math.floor(getNumRawDataModules(ver) / 8) -
  ECC_CODEWORDS_PER_BLOCK[ver]! * NUM_ERROR_CORRECTION_BLOCKS[ver]!

/** 整列パターンの中心座標一覧 (バージョン 1 は無し)。 */
const getAlignmentPatternPositions = (ver: number): number[] => {
  if (ver === 1) return []
  const numAlign = Math.floor(ver / 7) + 2
  const step = Math.ceil((ver * 4 + 4) / (numAlign * 2 - 2)) * 2
  const result = [6]
  for (let pos = ver * 4 + 10; result.length < numAlign; pos -= step) {
    result.splice(1, 0, pos)
  }

  return result
}

// ── GF(256) 演算 (原始多項式 0x11D) ──────────────────────────────────
const GF_EXP = new Uint8Array(512)
const GF_LOG = new Uint8Array(256)
{
  let x = 1
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x
    GF_LOG[x] = i
    x <<= 1
    if (x & 0x100) x ^= 0x11d
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255]!
}

const gfMul = (a: number, b: number): number =>
  a === 0 || b === 0 ? 0 : GF_EXP[GF_LOG[a]! + GF_LOG[b]!]!

/** 次数 `degree` の Reed-Solomon 生成多項式 (係数配列、先頭が最高次=1)。 */
const rsGenPoly = (degree: number): number[] => {
  let poly = [1]
  for (let d = 0; d < degree; d++) {
    const next = new Array<number>(poly.length + 1).fill(0)
    for (let i = 0; i < poly.length; i++) {
      next[i] = next[i]! ^ poly[i]!
      next[i + 1] = next[i + 1]! ^ gfMul(poly[i]!, GF_EXP[d]!)
    }
    poly = next
  }

  return poly
}

/** データブロックの EC コードワードを計算する。 */
const rsEncode = (data: number[], ecLen: number): number[] => {
  const gen = rsGenPoly(ecLen)
  const res = new Array<number>(data.length + ecLen).fill(0)
  for (let i = 0; i < data.length; i++) res[i] = data[i]!
  for (let i = 0; i < data.length; i++) {
    const coef = res[i]!
    if (coef !== 0) {
      for (let j = 0; j < gen.length; j++) {
        res[i + j] = res[i + j]! ^ gfMul(gen[j]!, coef)
      }
    }
  }

  return res.slice(data.length)
}

/** バイトモードでデータをエンコードし、最小バージョン + データコードワードを返す。 */
const encodeToCodewords = (text: string): { version: number; data: number[] } => {
  const bytes = [...new TextEncoder().encode(text)]

  let version = -1
  for (let v = MIN_VERSION; v <= MAX_VERSION; v++) {
    const ccBits = v <= 9 ? 8 : 16
    const needBits = 4 + ccBits + bytes.length * 8
    if (needBits <= getNumDataCodewords(v) * 8) {
      version = v
      break
    }
  }
  if (version === -1) {
    throw new Error('qrcode: data too long')
  }

  const ccBits = version <= 9 ? 8 : 16
  const bits: number[] = []

  const put = (val: number, len: number): void => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1)
  }

  put(0b0100, 4) // バイトモード指示子
  put(bytes.length, ccBits)
  for (const b of bytes) put(b, 8)

  const capacityBits = getNumDataCodewords(version) * 8

  // 終端子 (最大 4 bit) + バイト境界までの 0 埋め
  put(0, Math.min(4, capacityBits - bits.length))
  while (bits.length % 8 !== 0) bits.push(0)

  const data: number[] = []
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j]!
    data.push(byte)
  }
  // パディングバイト (0xEC / 0x11 を交互に)
  for (let pad = 0xec; data.length < getNumDataCodewords(version); pad ^= 0xec ^ 0x11) {
    data.push(pad)
  }

  return { version, data }
}

/** データコードワードをブロック分割 → EC 付与 → インターリーブする。 */
const addEccAndInterleave = (version: number, data: number[]): number[] => {
  const numBlocks = NUM_ERROR_CORRECTION_BLOCKS[version]!
  const eccLen = ECC_CODEWORDS_PER_BLOCK[version]!
  const rawCodewords = Math.floor(getNumRawDataModules(version) / 8)
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks)
  const shortBlockLen = Math.floor(rawCodewords / numBlocks)

  const blocks: number[][] = []
  for (let i = 0, k = 0; i < numBlocks; i++) {
    const datLen = shortBlockLen - eccLen + (i < numShortBlocks ? 0 : 1)
    const dat = data.slice(k, k + datLen)

    k += datLen

    const ecc = rsEncode(dat, eccLen)
    if (i < numShortBlocks) dat.push(0) // インターリーブ用の placeholder
    blocks.push(dat.concat(ecc))
  }

  const result: number[] = []
  for (let i = 0; i < blocks[0]!.length; i++) {
    for (let j = 0; j < blocks.length; j++) {
      // 短ブロックの placeholder 列はスキップ
      if (i !== shortBlockLen - eccLen || j >= numShortBlocks) {
        result.push(blocks[j]![i]!)
      }
    }
  }

  return result
}

/** 15 bit のフォーマット情報 (EC レベル M 固定 + マスク番号)。 */
const formatBits = (mask: number): number => {
  const data = (0b00 << 3) | mask // M = 0b00
  let rem = data
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >> 9) * 0x537)

  return ((data << 10) | rem) ^ 0x5412
}

/** 18 bit のバージョン情報 (バージョン 7 以上)。 */
const versionBits = (ver: number): number => {
  let rem = ver
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >> 11) * 0x1f25)

  return (ver << 12) | rem
}

const MASK_FNS: ((x: number, y: number) => boolean)[] = [
  (x, y) => (x + y) % 2 === 0,
  (_x, y) => y % 2 === 0,
  x => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
]

/**
 * テキストを QR コードのモジュール行列に変換する。
 * 戻り値 `modules[y][x]` — true が暗 (黒) モジュール。クワイエットゾーンは含まない。
 */
export const generateQrModules = (text: string): boolean[][] => {
  const { version, data } = encodeToCodewords(text)
  const codewords = addEccAndInterleave(version, data)
  const size = version * 4 + 17

  const modules: boolean[][] = Array.from({ length: size }, () =>
    new Array<boolean>(size).fill(false),
  )

  const isFunction: boolean[][] = Array.from({ length: size }, () =>
    new Array<boolean>(size).fill(false),
  )

  const setFn = (x: number, y: number, dark: boolean): void => {
    modules[y]![x] = dark
    isFunction[y]![x] = true
  }

  // ── ファインダ + セパレータ ──
  const drawFinder = (cx: number, cy: number): void => {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const x = cx + dx
        const y = cy + dy
        if (x < 0 || x >= size || y < 0 || y >= size) continue
        const dist = Math.max(Math.abs(dx), Math.abs(dy))

        setFn(x, y, dist !== 2 && dist !== 4)
      }
    }
  }

  // タイミングパターン
  for (let i = 0; i < size; i++) {
    setFn(6, i, i % 2 === 0)
    setFn(i, 6, i % 2 === 0)
  }

  drawFinder(3, 3)
  drawFinder(size - 4, 3)
  drawFinder(3, size - 4)

  // ── 整列パターン ──
  const alignPos = getAlignmentPatternPositions(version)
  for (const cy of alignPos) {
    for (const cx of alignPos) {
      // ファインダと重なる 3 隅はスキップ
      const corner =
        (cx === 6 && cy === 6) || (cx === 6 && cy === size - 7) || (cx === size - 7 && cy === 6)

      if (corner) continue
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          const dist = Math.max(Math.abs(dx), Math.abs(dy))

          setFn(cx + dx, cy + dy, dist !== 1)
        }
      }
    }
  }

  // ── フォーマット情報領域を予約 (中身は後で書く) ──
  // i=6 はタイミングパターン (row6 / col6) と交差するので予約しない
  // (フォーマット情報も row6 / col6 は使わない)。
  for (let i = 0; i < 9; i++) {
    if (i === 6) continue
    setFn(8, i, false)
    setFn(i, 8, false)
  }
  for (let i = 0; i < 8; i++) {
    setFn(size - 1 - i, 8, false)
    setFn(8, size - 1 - i, false)
  }
  setFn(8, size - 8, true) // 常時暗モジュール

  // ── バージョン情報 (v7+) ──
  if (version >= 7) {
    const vb = versionBits(version)
    for (let i = 0; i < 18; i++) {
      const bit = ((vb >>> i) & 1) === 1
      const a = size - 11 + (i % 3)
      const b = Math.floor(i / 3)

      setFn(a, b, bit)
      setFn(b, a, bit)
    }
  }

  // ── データコードワードを 2 列ずつジグザグ配置 ──
  // col 6 (タイミングパターン列) はスキップ: right が 6 に達したら 5 に詰めて
  // 以降の列ペアを (5,4)(3,2)(1,0) と続ける (ISO 18004 / Nayuki 実装と同じ)。
  let bitIdx = 0
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j
        const upward = ((right + 1) & 2) === 0
        const y = upward ? size - 1 - vert : vert
        if (isFunction[y]![x]) continue
        let dark = false
        if (bitIdx < codewords.length * 8) {
          dark = ((codewords[bitIdx >>> 3]! >>> (7 - (bitIdx & 7))) & 1) === 1
          bitIdx++
        }
        modules[y]![x] = dark
      }
    }
  }

  // ── マスク選択 (ペナルティ最小) ──
  let bestMask = 0
  let bestPenalty = Infinity
  let bestModules = modules
  for (let mask = 0; mask < 8; mask++) {
    const trial = modules.map(row => [...row])
    const fn = MASK_FNS[mask]!
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (!isFunction[y]![x] && fn(x, y)) trial[y]![x] = !trial[y]![x]
      }
    }
    drawFormatBits(trial, size, mask)

    const penalty = penaltyScore(trial, size)
    if (penalty < bestPenalty) {
      bestPenalty = penalty
      bestMask = mask
      bestModules = trial
    }
  }
  void bestMask

  return bestModules
}

/** 選択したマスクに対応するフォーマット情報 15 bit を配置する。 */
const drawFormatBits = (m: boolean[][], size: number, mask: number): void => {
  const bits = formatBits(mask)
  const bit = (i: number): boolean => ((bits >>> i) & 1) === 1

  for (let i = 0; i <= 5; i++) m[i]![8] = bit(i)
  m[7]![8] = bit(6)
  m[8]![8] = bit(7)
  m[8]![7] = bit(8)
  for (let i = 9; i < 15; i++) m[8]![14 - i] = bit(i)

  for (let i = 0; i < 8; i++) m[8]![size - 1 - i] = bit(i)
  for (let i = 8; i < 15; i++) m[size - 15 + i]![8] = bit(i)
  m[size - 8]![8] = true
}

/** ISO 18004 のマスク評価ペナルティ (4 規則の合計)。低いほど良い。 */
const penaltyScore = (m: boolean[][], size: number): number => {
  let score = 0

  // 規則 1: 同色 5 連以上
  for (let y = 0; y < size; y++) {
    let runColor = m[y]![0]!
    let runLen = 1
    for (let x = 1; x < size; x++) {
      if (m[y]![x] === runColor) {
        runLen++
        if (runLen === 5) score += 3
        else if (runLen > 5) score += 1
      } else {
        runColor = m[y]![x]!
        runLen = 1
      }
    }
  }
  for (let x = 0; x < size; x++) {
    let runColor = m[0]![x]!
    let runLen = 1
    for (let y = 1; y < size; y++) {
      if (m[y]![x] === runColor) {
        runLen++
        if (runLen === 5) score += 3
        else if (runLen > 5) score += 1
      } else {
        runColor = m[y]![x]!
        runLen = 1
      }
    }
  }

  // 規則 2: 2x2 同色ブロック
  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const c = m[y]![x]
      if (c === m[y]![x + 1] && c === m[y + 1]![x] && c === m[y + 1]![x + 1]) {
        score += 3
      }
    }
  }

  // 規則 3: ファインダ類似パターン (1:1:3:1:1 + 余白)
  const pattern = [true, false, true, true, true, false, true]

  const matchAt = (cells: boolean[], i: number): boolean => {
    for (let k = 0; k < 7; k++) if (cells[i + k] !== pattern[k]) return false
    const before = cells.slice(Math.max(0, i - 4), i)
    const after = cells.slice(i + 7, i + 11)
    const allLight = (arr: boolean[]): boolean => arr.length === 4 && arr.every(c => !c)

    return allLight(before) || allLight(after)
  }

  for (let y = 0; y < size; y++) {
    const row = m[y]!
    for (let x = 0; x + 7 <= size; x++) if (matchAt(row, x)) score += 40
  }
  for (let x = 0; x < size; x++) {
    const col: boolean[] = []
    for (let y = 0; y < size; y++) col.push(m[y]![x]!)
    for (let y = 0; y + 7 <= size; y++) if (matchAt(col, y)) score += 40
  }

  // 規則 4: 暗モジュール比率の偏り
  let dark = 0
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (m[y]![x]) dark++
  const total = size * size
  const percent = (dark * 100) / total
  const k = Math.floor(Math.abs(percent - 50) / 5)

  score += k * 10

  return score
}
